import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { LuBanknote } from 'react-icons/lu';

function AdminSystemSettingsPage() {
  const [settings, setSettings] = useState({
    default_language: 'ru',
    default_currency: 'uah',
    show_language_currency_switcher: true,
    active_languages: ['ru', 'ua', 'en', 'cn'],
    active_currencies: ['uah', 'rub', 'usd']
  });
  const [currencyRates, setCurrencyRates] = useState({
    UAH: { rate_to_usd: 40.0, updated_at: null },
    RUB: { rate_to_usd: 100.0, updated_at: null },
    USD: { rate_to_usd: 1.0, updated_at: null }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRates, setSavingRates] = useState(false);
  const [error, setError] = useState(null);
  const [ratesError, setRatesError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [ratesSaveStatus, setRatesSaveStatus] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const [settingsResponse, ratesResponse] = await Promise.all([
          fetch('/api/admin/system-settings', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch('/api/admin/currency-rates', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (!settingsResponse.ok) throw new Error('Не удалось загрузить настройки');
        const settingsData = await settingsResponse.json();
        // Добавляем fallback значения для active_languages и active_currencies
        setSettings({
          ...settingsData,
          active_languages: settingsData.active_languages || ['ru', 'ua', 'en', 'cn'],
          active_currencies: settingsData.active_currencies || ['uah', 'rub', 'usd']
        });
        
        if (ratesResponse.ok) {
          const ratesData = await ratesResponse.json();
          setCurrencyRates((prev) => ratesData.rates || prev);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleLanguage = (lang) => {
    setSettings(prev => {
      const current = prev.active_languages || [];
      const newLangs = current.includes(lang)
        ? current.filter(l => l !== lang)
        : [...current, lang];
      // Убеждаемся, что хотя бы один язык активен
      if (newLangs.length === 0) {
        return prev;
      }
      return { ...prev, active_languages: newLangs };
    });
  };

  const handleToggleCurrency = (curr) => {
    setSettings(prev => {
      const current = prev.active_currencies || [];
      const newCurrs = current.includes(curr)
        ? current.filter(c => c !== curr)
        : [...current, curr];
      // Убеждаемся, что хотя бы одна валюта активна
      if (newCurrs.length === 0) {
        return prev;
      }
      return { ...prev, active_currencies: newCurrs };
    });
  };

  const handleRateChange = (currency, value) => {
    setCurrencyRates(prev => ({
      ...prev,
      [currency]: {
        ...prev[currency],
        rate_to_usd: parseFloat(value) || 0
      }
    }));
  };

  const handleRatesSubmit = async (e) => {
    e.preventDefault();
    setSavingRates(true);
    setRatesError(null);
    setRatesSaveStatus(null);
    
    try {
      const response = await fetch('/api/admin/currency-rates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rates: currencyRates })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка сохранения курсов');
      
      setRatesSaveStatus('success');
      setTimeout(() => setRatesSaveStatus(null), 2000);
    } catch (e) {
      setRatesError(e.message);
      setRatesSaveStatus('error');
    } finally {
      setSavingRates(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaveStatus(null);
    
    try {
      const response = await fetch('/api/admin/system-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка сохранения');
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      setError(e.message);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="admin-page-content">
        <section><div className="loading-mini">Загрузка...</div></section>
      </main>
    );
  }

  return (
    <main className="admin-page-content">
      <section>
        <h2 className="admin-page-title">Системные настройки</h2>
        <p className="admin-description-text">
          Настройте язык и валюту по умолчанию для новых пользователей. Эти настройки будут применяться при регистрации.
        </p>
        
        {error && <div className="message-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="settings-form-group">
            <label htmlFor="default_language" className="settings-label">
              Язык по умолчанию
            </label>
            <select
              id="default_language"
              value={settings.default_language}
              onChange={(e) => handleChange('default_language', e.target.value)}
              className="settings-select"
            >
              <option value="ru">Русский</option>
              <option value="ua">Українська</option>
              <option value="cn">中文</option>
              <option value="en">English</option>
            </select>
            <p className="settings-hint">Этот язык будет установлен для всех новых пользователей при регистрации.</p>
          </div>

          <div className="settings-form-group">
            <label htmlFor="default_currency" className="settings-label">
              Валюта по умолчанию
            </label>
            <select
              id="default_currency"
              value={settings.default_currency}
              onChange={(e) => handleChange('default_currency', e.target.value)}
              className="settings-select"
            >
              <option value="uah">₴ UAH (Гривна)</option>
              <option value="rub">₽ RUB (Рубль)</option>
              <option value="usd">$ USD (Доллар)</option>
            </select>
            <p className="settings-hint">Эта валюта будет установлена для всех новых пользователей при регистрации.</p>
          </div>

          <div className="settings-form-group">
            <label htmlFor="show_language_currency_switcher" className="settings-label">
              <input
                type="checkbox"
                id="show_language_currency_switcher"
                checked={settings.show_language_currency_switcher}
                onChange={(e) => handleChange('show_language_currency_switcher', e.target.checked)}
                className="settings-checkbox"
                style={{ marginRight: '8px' }}
              />
              Показывать переключатели языка и валюты в Dashboard
            </label>
            <p className="settings-hint">Если отключено, пользователи не смогут изменять язык и валюту в клиентской панели.</p>
          </div>

          {/* Активные языки */}
          <div className="settings-form-group" style={{ marginTop: '30px' }}>
            <label className="settings-label" style={{ fontWeight: 'bold', marginBottom: '15px', display: 'block' }}>
              Активные языки
            </label>
            <p className="settings-hint" style={{ marginBottom: '15px' }}>
              Выберите языки, которые будут доступны пользователям для выбора. Должен быть выбран хотя бы один язык.
            </p>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '15px' }}>
              {[
                { code: 'ru', name: '🇷🇺 RU' },
                { code: 'ua', name: '🇺🇦 UA' },
                { code: 'en', name: '🇬🇧 EN' },
                { code: 'cn', name: '🇨🇳 CN' }
              ].map(lang => (
                <label key={lang.code} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', backgroundColor: (settings.active_languages || []).includes(lang.code) ? 'var(--accent-color)' : 'var(--bg-secondary)', color: (settings.active_languages || []).includes(lang.code) ? 'white' : 'var(--text-primary)', transition: 'all 0.2s' }}>
                  <input
                    type="checkbox"
                    checked={(settings.active_languages || []).includes(lang.code)}
                    onChange={() => handleToggleLanguage(lang.code)}
                    style={{ display: 'none' }}
                  />
                  <span>{lang.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Активные валюты */}
          <div className="settings-form-group" style={{ marginTop: '30px' }}>
            <label className="settings-label" style={{ fontWeight: 'bold', marginBottom: '15px', display: 'block' }}>
              Активные валюты
            </label>
            <p className="settings-hint" style={{ marginBottom: '15px' }}>
              Выберите валюты, которые будут доступны пользователям для выбора. Должна быть выбрана хотя бы одна валюта.
            </p>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '15px' }}>
              {[
                { code: 'uah', name: '₴ UAH' },
                { code: 'rub', name: '₽ RUB' },
                { code: 'usd', name: '$ USD' }
              ].map(curr => (
                <label key={curr.code} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', backgroundColor: (settings.active_currencies || []).includes(curr.code) ? 'var(--accent-color)' : 'var(--bg-secondary)', color: (settings.active_currencies || []).includes(curr.code) ? 'white' : 'var(--text-primary)', transition: 'all 0.2s' }}>
                  <input
                    type="checkbox"
                    checked={(settings.active_currencies || []).includes(curr.code)}
                    onChange={() => handleToggleCurrency(curr.code)}
                    style={{ display: 'none' }}
                  />
                  <span>{curr.name}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="settings-actions">
            <button 
              type="submit" 
              className="btn" 
              style={{ maxWidth: '200px' }}
              disabled={saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
            {saveStatus === 'success' && (
              <div className="message-success">Сохранено!</div>
            )}
          </div>
        </form>

        {/* Раздел управления курсами валют */}
        <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '1px solid var(--glass-border)' }}>
          <h2 className="admin-page-title" style={{ marginBottom: '10px' }}>
            <LuBanknote style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Курсы валют
          </h2>
          <p className="admin-description-text" style={{ marginBottom: '30px' }}>
            Установите курсы валют к USD. Эти курсы используются для конвертации баланса пользователей при переключении валюты.
          </p>
          
          {ratesError && <div className="message-error">{ratesError}</div>}
          
          <form onSubmit={handleRatesSubmit}>
            <div className="settings-form-group">
              <label className="settings-label" style={{ fontWeight: 'bold', marginBottom: '15px', display: 'block' }}>
                Курс к USD (1 USD = X валюты)
              </label>
              
              {['UAH', 'RUB'].map(currency => (
                <div key={currency} style={{ marginBottom: '20px' }}>
                  <label htmlFor={`rate_${currency}`} className="settings-label" style={{ display: 'block', marginBottom: '8px' }}>
                    {currency === 'UAH' ? '₴ UAH (Гривна)' : '₽ RUB (Рубль)'}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>1 USD =</span>
                    <input
                      type="number"
                      id={`rate_${currency}`}
                      step="0.01"
                      min="0.01"
                      value={currencyRates[currency]?.rate_to_usd || 0}
                      onChange={(e) => handleRateChange(currency, e.target.value)}
                      className="settings-input"
                      style={{ 
                        flex: 1, 
                        maxWidth: '200px',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--glass-border)',
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)'
                      }}
                      required
                    />
                    <span style={{ color: 'var(--text-secondary)' }}>{currency}</span>
                  </div>
                  {currencyRates[currency]?.updated_at && (
                    <p className="settings-hint" style={{ marginTop: '5px', fontSize: '12px' }}>
                      Обновлено: {new Date(currencyRates[currency].updated_at).toLocaleString('ru-RU')}
                    </p>
                  )}
                </div>
              ))}
              
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                <strong>Примечание:</strong> USD всегда равен 1.0 и не может быть изменен.
              </div>
            </div>
            
            <div className="settings-actions">
              <button 
                type="submit" 
                className="btn" 
                style={{ maxWidth: '200px' }}
                disabled={savingRates}
              >
                {savingRates ? 'Сохранение...' : 'Сохранить курсы'}
              </button>
              {ratesSaveStatus === 'success' && (
                <div className="message-success">Курсы сохранены!</div>
              )}
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

export default AdminSystemSettingsPage;

